/**
 * VISITOR PASSES — the wire shapes (resident-portal Plan 2).
 *
 * Mirrors the ACTUAL responses of lobbero_be/src/CRUD/visitor-passes.js and
 * POST /visitor-log/from-pass (src/CRUD/visitor-log.js), not the plan text. If
 * the server changes a key, change it here first.
 *
 * A pass is a standing "no need to ring up" for a named visitor, stored in its
 * own table (migration 115). Times are ISO instants; `local_date`, `valid_from`,
 * `valid_until`, `paused_until` and `skip_dates` are calendar dates
 * ('YYYY-MM-DD') in the BUILDING's zone; `window_start` / `window_end` are local
 * wall-clock 'HH:MM' in the building's zone (end <= start crosses midnight).
 */

export type PassCategory =
  | 'family'
  | 'friend'
  | 'cleaner'
  | 'carer'
  | 'babysitter'
  | 'dog_walker'
  | 'contractor'
  | 'other';

export type PassScheduleKind = 'once' | 'weekly';

/** Human acts only — stored. 'pending' (migration 125, RP-4.9): a resident's
 *  pass at a building where a manager approves them, not yet answered. */
export type PassStatus = 'active' | 'paused' | 'revoked' | 'pending';

/** Derived at read time — never stored (active -> success, paused / scheduled /
 *  pending -> warning, ended -> neutral). */
export type PassState = 'active' | 'paused' | 'scheduled' | 'ended' | 'pending';

/** The building's answer to a pending pass (125). null = never needed one, or
 *  still waiting. A rejected pass is ended. */
export type PassDecision = 'approved' | 'rejected' | null;

/** Where a sign-in fell against the window (server clock). */
export type PassWindowState = 'in_window' | 'early' | 'late';

export type PassArrivalState = 'not_arrived' | 'inside' | 'left';

export interface PassOccurrence {
  local_date: string;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
}

export interface PassPerson {
  first_name: string;
  last_name: string;
}

/** One pass as a staff list / detail returns it. */
export interface VisitorPass {
  pass_id: string;
  first_name: string;
  last_name: string;
  company: string;
  category: PassCategory;
  max_party: number;
  note: string;
  unit_id: number;
  unit_label: string;
  building_id: number;
  host_occupancy_id: string;
  host: PassPerson;
  schedule_kind: PassScheduleKind;
  valid_from: string;
  valid_until: string;
  /** ISO weekdays, 1 = Monday … 7 = Sunday. Empty for a one-time pass. */
  days: number[];
  interval_weeks: number;
  window_start: string | null;
  window_end: string | null;
  skip_dates: string[];
  status: PassStatus;
  paused_until: string | null;
  state: PassState | null;
  decision?: PassDecision;
  decided_at?: string | null;
  /** The manager's note to the household with the decision ('' = none). */
  decision_note?: string;
  created_via: 'resident' | 'staff';
  created_by_me: boolean;
  created_at: string;
  updated_at: string;
  next: PassOccurrence | null;
  /** May the caller change it (edit, pause, resume, skip)? The server's
   *  answer, from their role and the building's desk setting (migration 118). */
  can_edit?: boolean;
  /** May the caller end it? */
  can_end?: boolean;
  /** May the caller approve or decline it (a manager, on a pending pass)? */
  can_decide?: boolean;
}

/** How far the front desk may write passes at a building (migration 118). */
export type ConciergePassAccess = 'off' | 'once' | 'all';

/** The caller's reach at one building, sent with the desk's reads. */
export interface PassAccess {
  /** 'manage' = a manager or the owner; otherwise the building's desk setting. */
  level: 'manage' | ConciergePassAccess | null;
  can_add: boolean;
  /** Only one-time visits for today or tomorrow (the 'once' setting). */
  once_only: boolean;
  /** The building's desk setting itself. */
  setting: ConciergePassAccess;
}

export interface PassArrival {
  id: number;
  arrived_at: string;
  departed_at: string | null;
  window_state: PassWindowState | null;
  party: number;
  kind: string;
}

export interface VisitorPassDetail extends VisitorPass {
  zone: string;
  today: string;
  occurrences: PassOccurrence[];
  arrivals: PassArrival[];
  limits: PassLimits;
}

/** The server's limits, handed to the editor so its inputs never keep a
 *  second copy of them. */
export interface PassLimits {
  max_live_per_unit: number;
  once_max_ahead_days: number;
  weekly_max_days: number;
  note_max: number;
  skip_max: number;
}

export interface PassListResponse {
  rows: VisitorPass[];
  next_cursor: string | null;
  /** The building's calendar date — what "today" means for every date rule. */
  today: string;
  zone: string;
  limits: PassLimits;
  pass_access?: PassAccess;
  /** The building's own switch as stored (Plan 3 register). */
  passes_enabled?: boolean;
  /** The building is active — with the switch, "accepts passes". */
  building_active?: boolean;
  /** A manager approves every pass a resident adds here (125). */
  approval_required?: boolean;
  /** Live passes here waiting for that approval. */
  pending_count?: number;
}

/** One row of the desk's Expected today screen. */
export interface ExpectedRow {
  pass_id: string;
  first_name: string;
  last_name: string;
  company: string;
  category: PassCategory;
  max_party: number;
  party_remaining: number;
  note: string;
  unit_id: number;
  unit_label: string;
  host: PassPerson;
  local_date: string;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  /** null when another desk day is viewed, or the visit is not signable now. */
  window_state: PassWindowState | null;
  arrival_state: PassArrivalState;
  arrival_id: number | null;
  arrived_at: string | null;
  departed_at: string | null;
  created_via: 'resident' | 'staff';
}

export interface ExpectedResponse {
  desk_day: { date: string; from: string; to: string; zone: string };
  generated_at: string;
  /** The building's live switch: false hides the Expected card immediately. */
  passes_enabled: boolean;
  /** What this caller may add here — drives "Add expected visitor". */
  pass_access?: PassAccess;
  rows: ExpectedRow[];
}

/** What the editor sends (create and edit share it; the host is create-only). */
export interface PassSpecInput {
  first_name: string;
  last_name?: string;
  company?: string;
  category: PassCategory | '';
  schedule_kind: PassScheduleKind;
  valid_from: string;
  valid_until?: string;
  days?: number[];
  interval_weeks?: number;
  window_start?: string | null;
  window_end?: string | null;
  max_party?: number;
  note?: string;
}

/** One home the resident editor may pick (Plan 4). */
export interface PassHome {
  occupancy_id: string;
  unit_id: number | null;
  unit_label: string;
  building_name: string;
  building_timezone: string;
  /** The building's desk date ('YYYY-MM-DD'): yesterday before 07:00. The
   *  earliest day a new pass may start (Plan 8, migration 129). */
  desk_day?: string;
}

/** The 400 every pass route answers: `message` + `field` (the FIRST bad field,
 *  what serverFieldError reads) + `fields` (every bad field at once). */
export interface PassFieldErrors {
  message: string;
  field: string;
  fields: Record<string, string>;
  code?: string;
}

export type PassAction = 'pause' | 'resume' | 'revoke' | 'skip' | 'unskip';

export const PASS_CATEGORIES: readonly PassCategory[] = [
  'family',
  'friend',
  'cleaner',
  'carer',
  'babysitter',
  'dog_walker',
  'contractor',
  'other',
];

export const PASS_CATEGORY_LABELS: Record<PassCategory, string> = {
  family: 'Family',
  friend: 'Friend',
  cleaner: 'Cleaner',
  carer: 'Carer',
  babysitter: 'Babysitter',
  dog_walker: 'Dog walker',
  contractor: 'Contractor',
  other: 'Other',
};

/* ──────────────  The resident's side (Plan 3, GET /tenant/passes)  ────────────── */

/** The resident's derived state: the staff states plus 'off' — the building has
 *  switched visitor passes off (the pass is kept, nothing is expected). */
export type ResidentPassState = PassState | 'off';

/** One of the resident's homes, with whether its building takes passes. */
export interface ResidentPassHome {
  occupancy_id: string;
  unit_id: number | null;
  unit_label: string;
  building_id: number;
  building_name: string;
  building_timezone: string;
  passes_enabled: boolean;
}

/**
 * One pass of the resident's household (src/passes/serialize.js forResident).
 * Narrower than the staff shape on purpose: no host, no staff ids — only whether
 * the building added it (created_via 'staff') or the caller did (created_by_me).
 */
export interface ResidentPass {
  pass_id: string;
  first_name: string;
  last_name: string;
  company: string;
  category: PassCategory;
  max_party: number;
  note: string;
  unit_id: number;
  unit_label: string;
  building_id: number;
  building_timezone: string;
  schedule_kind: PassScheduleKind;
  valid_from: string;
  valid_until: string;
  days: number[];
  interval_weeks: number;
  window_start: string | null;
  window_end: string | null;
  skip_dates: string[];
  status: PassStatus;
  paused_until: string | null;
  state: ResidentPassState;
  decision?: PassDecision;
  decided_at?: string | null;
  decision_note?: string;
  created_via: 'resident' | 'staff';
  created_by_me: boolean;
  created_at: string;
  updated_at: string;
  /** Upcoming visits, soonest first: up to 3 in the list, 8 on the detail. */
  next: PassOccurrence[];
  /** 'YYYY-MM-DD' when the pass has not begun (or has nothing upcoming in
   *  range yet) — "Starts <date>". */
  starts_on: string | null;
  /** Plan 6 (migration 126): the pass is shared as a link right now — since
   *  when, and how many times the guest's page has been opened. The link
   *  itself is shown once, when it is made, and never again. */
  shared?: PassShare | null;
  /** Plan 6: the pass can be shared (live, not waiting for approval, at a
   *  building that takes passes). */
  can_share?: boolean;
}

export interface PassShare {
  since: string | null;
  opened: number;
}

/** POST /tenant/passes/:id/link — the link, ONCE. */
export interface PassShareCreated {
  url: string;
  link_id: string;
  pass: ResidentPass | null;
}

/**
 * The guest's pass page (GET /api/pass-links/:token, Plan 6 RP-6.3). First
 * names only; no unit, no note, no ids. The schedule's raw fields, so the
 * page writes the sentence with describeSchedule, in the building's clock.
 */
export interface PublicPass {
  guest_first_name: string;
  host_first_name: string;
  building: { name: string; address: string; timezone: string };
  today: string;
  schedule: {
    schedule_kind: PassScheduleKind;
    valid_from: string;
    valid_until: string | null;
    days: number[];
    interval_weeks: number;
    window_start: string | null;
    window_end: string | null;
    skip_dates: string[];
    status: PassStatus;
    paused_until: string | null;
  };
  next: PassOccurrence | null;
  /** The organisation is suspended (Plan 3 F1): the desk cannot sign anyone
   *  in, so there is no code. Comes back by itself on restore. */
  suspended?: boolean;
  /** null only while `suspended`. */
  qr_data_url: string | null;
}

export interface ResidentPassList {
  homes: ResidentPassHome[];
  passes: ResidentPass[];
  limits: PassLimits;
}

/** One arrival on a pass, as the household sees it: when, how many, and where
 *  it fell against the window. No names, no staff. */
export interface ResidentPassVisit {
  arrived_at: string | null;
  departed_at: string | null;
  window_state: PassWindowState | null;
  party_size: number;
}

export interface ResidentPassDetail {
  pass: ResidentPass;
  visits: ResidentPassVisit[];
  limits: PassLimits;
}

/**
 * "Lobbero remembers" (resident-portal Plan 5, RP-5.3): a visitor who keeps
 * coming on the same weekdays, offered as a regular. Derived by the server
 * from the household's own visitor list; FIRST NAME ONLY. `key_token` is
 * opaque — sent back as-is to dismiss, never read.
 */
export interface PassSuggestion {
  key_token: string;
  occupancy_id: string;
  unit_label: string;
  building_name: string;
  building_timezone: string;
  first_name: string;
  /** ISO weekdays, 1 = Monday. */
  days: number[];
  /** 'HH:MM' in the building's zone, or both null for "Any time". */
  window_start: string | null;
  window_end: string | null;
  /** What "Make regular" proposes: from the building's today, for three months. */
  valid_from: string;
  valid_until: string;
  evidence: {
    visits: number;
    /** Distinct days they came on the suggested weekdays. */
    dates: number;
    weeks: number;
    last_visit: string;
  };
}
